import type { QuizForInstance, StartQuizResponse, SubmitQuizResponse } from "@repo/shared/types";
import { useState } from "react";
import { toast } from "sonner";
import { usePublicAnsweringSubjects } from "@/lib/hooks/use-public-flow/use-answering-subjects";
import {
  usePublicQuizzes,
  useStartPublicQuiz,
  useSubmitPublicQuiz,
} from "@/lib/hooks/use-public-flow/use-quiz";
import { useT } from "@/lib/i18n/use-t";
import { QuizModal } from "@/pages/clients/detail/flows/instance/quiz-modal";
import { QuizWidget } from "@/pages/clients/detail/flows/instance/quiz-widget";
import { T } from "./quiz-renderer.i18n";

type QuizRendererProps = {
  component: { id: string; config?: string };
  isCompleted: boolean;
  language: string;
  hasValidationError?: boolean;
};

/**
 * Public flow surface for one `quiz` component. Imports the SAME widget and
 * modal the advisor wizard uses — never a copy — so the client sees exactly
 * what the advisor does.
 *
 * There is no override here. That is the whole point of the gate: only an
 * advisor can clear a quiz the client failed out of.
 */
export function QuizRenderer({
  component,
  isCompleted,
  language,
  hasValidationError,
}: QuizRendererProps) {
  const t = useT(T);
  const { quizzes, isLoading } = usePublicQuizzes();
  const {
    attribution,
    sessionSubjectId,
    isLoading: subjectsLoading,
  } = usePublicAnsweringSubjects();
  const startQuiz = useStartPublicQuiz();
  const submitQuiz = useSubmitPublicQuiz();

  const [attempt, setAttempt] = useState<StartQuizResponse | null>(null);
  // QL-505 — the member whose attempt is in flight, so submit addresses the
  // same member the flow of `standing.members` line's onStart started for.
  const [attemptSubjectId, setAttemptSubjectId] = useState<string | undefined>(undefined);

  // QL-13 — only the presentation bits. The questions live in this same config
  // server-side but are never shipped here: they are drawn per attempt.
  let config: {
    style?: "none" | "border" | "box";
    title?: string;
    description?: string;
  } = {};
  try {
    config = component.config ? JSON.parse(component.config) : {};
  } catch {
    // Invalid config, use defaults
  }

  // QL-505 — wait for the answering-subjects query too (like the advisor
  // wizard's `wizard-quiz.tsx`), so a members-mode client never flashes as a
  // single subject before `attribution`/`sessionSubjectId` load.
  if (isLoading || subjectsLoading) return null;

  // The component id IS the quiz id.
  const standing: QuizForInstance | undefined = quizzes?.find(
    (entry) => entry.quiz.id === component.id,
  );

  // QL-505 — a group session (no `sessionSubjectId`) viewing a members-mode
  // group sees every member line read-only; only that member's own link
  // (`sessionSubjectId` set) may act on their own line.
  const isMembersMode = attribution?.kind === "members";
  const readOnlyMembers = isMembersMode && sessionSubjectId == null;

  const handleStart = async (subjectId?: string) => {
    try {
      const result = await startQuiz.mutateAsync({ quizId: component.id, subjectId });
      setAttemptSubjectId(subjectId);
      setAttempt(result);
    } catch {
      toast.error(t.startFailed);
    }
  };

  const handleSubmit = async (
    answers: Array<{ questionId: string; selectedOptionId: string }>,
  ): Promise<SubmitQuizResponse> => {
    return submitQuiz.mutateAsync({ quizId: component.id, answers, subjectId: attemptSubjectId });
  };

  return (
    <>
      <QuizWidget
        standing={standing}
        onStart={handleStart}
        isStarting={startQuiz.isPending}
        disabled={isCompleted}
        style={config.style}
        title={config.title}
        description={config.description}
        language={language}
        hasValidationError={hasValidationError}
        overrideByName={standing?.override?.byUserName ?? null}
        overrideReason={standing?.override?.reason ?? null}
        attribution={attribution ?? undefined}
        sessionSubjectId={sessionSubjectId}
        readOnlyMembers={readOnlyMembers}
      />

      {attempt && standing && (
        <QuizModal
          quizName={standing.quiz.name}
          attempt={attempt}
          onSubmit={handleSubmit}
          onClose={() => setAttempt(null)}
        />
      )}
    </>
  );
}
